/**
 * One short themed show per clock hour, played over the family calendar.
 */

export type ShowId =
  | 'blizzard'
  | 'cupid'
  | 'leprechaun'
  | 'rain'
  | 'bloom'
  | 'beach'
  | 'sun'
  | 'campfire'
  | 'leaf-pile'
  | 'monsters'
  | 'turkeys'
  | 'sleigh';

export interface ShowSpec {
  id: ShowId;
  month: number;
  ms: number;
  fullScreen: boolean;
}

export const SHOWS: readonly ShowSpec[] = [
  { id: 'blizzard', month: 1, ms: 14000, fullScreen: true },
  { id: 'cupid', month: 2, ms: 12000, fullScreen: false },
  { id: 'leprechaun', month: 3, ms: 14000, fullScreen: false },
  { id: 'rain', month: 4, ms: 12000, fullScreen: true },
  { id: 'bloom', month: 5, ms: 14000, fullScreen: false },
  { id: 'beach', month: 6, ms: 16000, fullScreen: false },
  { id: 'sun', month: 7, ms: 12000, fullScreen: false },
  { id: 'campfire', month: 8, ms: 16000, fullScreen: false },
  { id: 'leaf-pile', month: 9, ms: 14000, fullScreen: false },
  { id: 'monsters', month: 10, ms: 16000, fullScreen: false },
  { id: 'turkeys', month: 11, ms: 12000, fullScreen: false },
  { id: 'sleigh', month: 12, ms: 12000, fullScreen: false },
];

const BY_MONTH = new Map(SHOWS.map((show) => [show.month, show]));
const BY_ID = new Map(SHOWS.map((show) => [show.id, show]));

export function showForMonth(month: number): ShowSpec {
  const show = BY_MONTH.get(month);
  if (!show) throw new Error(`No hourly show for month ${month}`);
  return show;
}

export function showDuration(id: ShowId): number {
  return BY_ID.get(id)?.ms ?? 12000;
}

export function isFullScreen(id: ShowId): boolean {
  return BY_ID.get(id)?.fullScreen ?? false;
}

/** `2026-10-14T15` so a refresh in the same hour does not replay. */
export function hourKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}T${date.getHours()}`;
}

export function shouldPlayHourly(now: Date, lastKey: string | null): boolean {
  return lastKey !== hourKey(now);
}

/** Milliseconds until the next top of the hour. Exactly on the hour waits a full hour. */
export function msUntilNextHour(date: Date): number {
  const next = new Date(date);
  next.setMinutes(0, 0, 0);
  next.setHours(next.getHours() + 1);
  return next.getTime() - date.getTime();
}

/** `?show=1` plays the current month's show immediately and is not stored. */
export function showOverride(search: string): boolean {
  const raw = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get('show');
  return raw === '1' || raw === 'now';
}
